import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Task } from '@forecastify/shared';
import { Button, Icon, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Pick another task — for linking, and for naming a predecessor.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A SEARCH, NOT A LIST, AND THAT IS THE ONE PLACE THIS DEPARTS FROM THE MOCKUP.** `main.jsx`
 * builds `linkMenu` from *every* task in the workspace and renders it as a dropdown. It has 28 of
 * them. A real workspace has thousands, and a menu that long is unusable before it is slow — so the
 * query goes to the server, which already accepts `?search=` over key and title.
 *
 * ⚠️⚠️ **INLINE, UNDER THE INPUT — NOT A DIALOG IN THE MIDDLE OF THE SCREEN.** It shipped as a
 * centred modal and was reported: the mockup's `linkMenu` drops its candidates directly beneath the
 * control that opened it, and a modal is the wrong weight for a choice you make three of in a row.
 * Nothing about the search changed; only where the results are drawn.
 *
 * ⚠️ **Presentational: it is handed `onSearch` and knows nothing about the session.** Every screen
 * in this app renders props; the route fetches. `projects-screen.tsx` records what happened the one
 * time a screen reached for router context instead.
 *
 * ⚠️ **MOUNTED WHEN IT IS OPEN, rather than taking an `open` prop.** Unmounting is what clears the
 * last search, which the dialog version needed a second effect to do; and a panel that renders
 * nothing while closed cannot leave a stale result set on screen for the next relation.
 *
 * ⚠️ **`exclude` covers the task itself AND everything already related.** The server refuses both
 * (`self`, `duplicate`), and offering a choice that will be refused is worse than not offering it.
 *
 * ⚠️⚠️ **THE PROJECT SCOPE IS NOT FILTERED HERE — THE QUERY IS.** A task inside a project relates
 * only to that project's tasks, and a task with no project only to other tasks with no project. The
 * search passes `relatableTo` and the server applies the predicate, so this component never learns
 * the rule and cannot disagree with it. What it does carry is `scopeHint`: a list that is short for
 * a reason should say the reason, or it reads as a broken search.
 */
export function TaskPicker({
  title,
  exclude,
  scopeHint,
  onSearch,
  onPick,
  onClose,
  leading,
}: {
  /** Already translated — the dialog's own name, which differs for a link and a dependency. */
  readonly title: string;
  /** Task ids that must not be offered. */
  readonly exclude: readonly string[];
  /** Already translated — why the candidates are limited to what they are. */
  readonly scopeHint: string;
  readonly onSearch: (query: string) => Promise<readonly Task[]>;
  readonly onPick: (task: Task) => void;
  readonly onClose: () => void;
  /**
   * ⚠️ **Anything the ADD needs that the search does not** — the dependency's lag, and nothing
   * else so far. It rides inside the panel because it belongs to the act of adding rather than to
   * the row being added: outside, it sat in the section's flow and applied to a picker that might
   * not be open.
   */
  readonly leading?: ReactNode;
}) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<readonly Task[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  /**
   * ⚠️⚠️ **DEBOUNCED, AND THE STALE RESPONSE IS DROPPED BY TOKEN RATHER THAN BY TIME.** Two
   * searches in flight can return out of order, so the later query's rows get overwritten by the
   * earlier query's — the classic symptom being a list that matches what you typed a moment ago.
   * The counter is compared on arrival; anything but the newest is discarded.
   */
  const latest = useRef(0);
  useEffect(() => {
    const token = latest.current + 1;
    latest.current = token;
    setIsSearching(true);

    const timer = setTimeout(() => {
      void onSearch(query).then((rows) => {
        if (latest.current !== token) return;
        setIsSearching(false);
        setResults(rows);
      });
    }, 200);

    return () => {
      clearTimeout(timer);
    };
    /* ⚠️ `onSearch` is deliberately absent: it is an inline arrow at the call site, so including
     * it would re-run this on every render of the detail — the loop `page-header.tsx` records. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const offered = results.filter((task) => !exclude.includes(task.id));

  return (
    /* ⚠️ A named `search` landmark: with the dialog gone there is no other way to say "these
       rows belong to the picker, not to the list behind it" — to a screen reader or to a test. */
    <div
      role="search"
      aria-label={title}
      /**
       * ⚠️⚠️ **IT FLOATS OVER THE SECTION NOW RATHER THAN PUSHING IT DOWN — 14 Sep.** Inline, the
       * panel displaced every row under it the moment it opened, so the list you were about to
       * link to jumped as the search appeared. The mockup drops its candidates over the content,
       * anchored to the button that opened them.
       *
       * ⚠️ **`absolute` against a `relative` parent the SECTION owns**, not a portal: it belongs to
       * the section's flow for focus and for Escape, and a portal would need its own dismiss and
       * its own position tracking to end up in the same place.
       *
       * ⚠️ **`right-0`, because the trigger is right-aligned in its heading.** A left-anchored
       * panel on a right-aligned button hangs off the far side of a narrow drawer.
       */
      className="border-input bg-popover absolute top-full right-0 z-20 mt-1 flex w-[380px] max-w-[calc(100vw-2rem)] flex-col gap-2 rounded-lg border p-2 shadow-lg"
    >
      <div className="flex items-center gap-2">
        <TextInput
          autoFocus
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onKeyDown={(event) => {
            /* ⚠️ Escape closes, because there is no backdrop to click any more. Without it the
               only way out of an inline panel is the button that opened it. */
            if (event.key === 'Escape') {
              event.preventDefault();
              onClose();
            }
          }}
          placeholder={title}
          aria-label={title}
          className="flex-1"
        />
        <Button variant="ghost" size="icon-sm" aria-label={t('common.close')} onClick={onClose}>
          <Icon name="x" size={14} />
        </Button>
      </div>

      {/* ⚠️ Above the results, not under them: it explains what is about to be listed, and a
          reader who has just seen three rows where they expected thirty has already formed an
          opinion by the time they reach the bottom of the box. */}
      <p className="text-muted-foreground text-[11.5px]">{scopeHint}</p>

      {leading}

      <div className="max-h-[240px] overflow-y-auto">
        {/* ⚠️ Three states, and none of them a blank pane — `state-rules.md`. "Searching" and
            "nothing matched" are different facts, and an empty box says neither. */}
        {isSearching ? (
          <p className="text-muted-foreground px-2 py-4 text-center text-[12px]">
            {t('common.loading')}
          </p>
        ) : offered.length === 0 ? (
          <p className="text-muted-foreground px-2 py-4 text-center text-[12px]">
            {t('task.picker.empty')}
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {offered.map((task) => (
              <li key={task.id}>
                <button
                  type="button"
                  className="hover:bg-accent/50 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left"
                  onClick={() => {
                    onPick(task);
                  }}
                >
                  <span
                    className="size-2 shrink-0 rounded-[3px]"
                    style={{ background: task.statusColor }}
                    aria-hidden
                  />
                  <span className="text-muted-foreground w-[64px] shrink-0 truncate text-[11.5px] tabular-nums">
                    {task.key}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[12.5px]">{task.title}</span>
                  {/* The status in WORDS beside the dot — colour is never the only carrier. */}
                  <span className="text-muted-foreground shrink-0 text-[11.5px]">
                    {task.statusName}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
